import { EditorSettingService } from '@affine/core/modules/editor-setting';
import { FeatureFlagService } from '@affine/core/modules/feature-flag';
import { ThemeEditorService } from '@affine/core/modules/theme-editor';
import {
  DARK_CANVAS_SPOTLIGHT_GRADIENT,
  LIGHT_CANVAS_SPOTLIGHT_GRADIENT,
} from '@blocksuite/affine-block-surface';
import {
  edgelessCanvasBackgroundVar,
  edgelessCanvasThemeStyle,
  minimapControlColorVar,
  minimapPanelBackgroundVar,
} from '@blocksuite/affine-shared/styles';
import { documentModeSelector, themeModeOf } from '@streamlain/theme';
import { isThemeName } from '@streamlain/theme/v2';
import { useLiveData, useServices } from '@toeverything/infra';
import { useTheme } from 'next-themes';
import { useEffect } from 'react';

const EDGELESS_PRESET_OVERRIDE_ID = 'sl-edgeless-preset-override';
const STRONG_CHIP_LABEL_OVERRIDE_ID = 'sl-strong-chip-label-override';
const BLUE_WHITE_MINIMAP_CONTROL_COLOR = '#77757d';
const BLUE_WHITE_CANVAS_CARD_SHADOW =
  '0 -1px 8px rgba(80, 144, 211, 0.12), 0 6px 12px rgba(2, 10, 22, 0.48), 0 18px 38px rgba(2, 10, 22, 0.58)';

// Chips on light document surfaces render on saturated fills (theme
// `chip/label/*`); their inline-styled icons and labels stay white.
const lightDocument = `html${documentModeSelector('light')}`;
const databaseViewports =
  ':is(.database-fullscreen-viewport, .database-live-presentation-viewport)';
const lightDocumentRule = (targets: string[], declarations: string) =>
  `${targets.map(target => `${lightDocument} ${target}`).join(',\n')} { ${declarations} }`;
const STRONG_CHIP_LABEL_CSS = [
  lightDocumentRule(
    [
      `${databaseViewports} affine-multi-tag-select svg path`,
      `${databaseViewports} affine-multi-tag-view svg path`,
      `${databaseViewports} data-view-group-title-select-view svg path`,
    ],
    'color: #ffffff;'
  ),
  lightDocumentRule(
    [`${databaseViewports} affine-multi-tag-select input::placeholder`],
    'color: #929292;'
  ),
  lightDocumentRule(
    [
      `${databaseViewports} data-view-group-title-select-view .tag`,
      `${databaseViewports} affine-multi-tag-view .select-selected`,
      '.blocksuite-portal data-view-group-title-select-view .tag',
    ],
    'color: #ffffff;'
  ),
].join('\n');

// Global CSS for Edgeless presets (applies regardless of App theme)
// Canvas colours come from the theme tables; a document may pin its canvas to
// a theme other than the app's.
const EDGELESS_BLUE_WHITE_CSS = `
  /* Edgeless Light Canvas - neutral paper */
  .affine-edgeless-viewport[data-edgeless-preset="light"],
  html[data-theme="light"] .affine-edgeless-viewport[data-edgeless-preset="system"] {
    ${edgelessCanvasThemeStyle('light')}
    ${minimapPanelBackgroundVar}: var(${edgelessCanvasBackgroundVar});
  }

  .affine-edgeless-viewport[data-edgeless-preset="light"] .edgeless-background,
  html[data-theme="light"] .affine-edgeless-viewport[data-edgeless-preset="system"] .edgeless-background {
    background-color: var(${edgelessCanvasBackgroundVar});
    background-image: ${LIGHT_CANVAS_SPOTLIGHT_GRADIENT};
  }

  /* Edgeless Dark Canvas - neutral graphite */
  .affine-edgeless-viewport[data-edgeless-preset="dark"],
  html[data-theme="dark"] .affine-edgeless-viewport[data-edgeless-preset="system"] {
    ${edgelessCanvasThemeStyle('dark')}
    ${minimapPanelBackgroundVar}: var(${edgelessCanvasBackgroundVar});
  }

  .affine-edgeless-viewport[data-edgeless-preset="dark"] .edgeless-background,
  html[data-theme="dark"] .affine-edgeless-viewport[data-edgeless-preset="system"] .edgeless-background {
    background-color: var(${edgelessCanvasBackgroundVar});
    background-image: ${DARK_CANVAS_SPOTLIGHT_GRADIENT};
  }

  /* Edgeless Blue-White Canvas - when app theme is blue-white and doc is automatic */
  html[data-theme="blue-white"] .affine-edgeless-viewport[data-edgeless-preset="system"],
  .affine-edgeless-viewport[data-edgeless-preset="blue-white"] {
    --streamlain-note-shadow-box: ${BLUE_WHITE_CANVAS_CARD_SHADOW};
    --streamlain-canvas-card-shadow: var(--streamlain-note-shadow-box);
  }

  html[data-theme="blue-white"] .affine-edgeless-viewport[data-edgeless-preset="system"] {
    ${edgelessCanvasThemeStyle('blue-white')}
    ${minimapPanelBackgroundVar}: var(${edgelessCanvasBackgroundVar});
    ${minimapControlColorVar}: ${BLUE_WHITE_MINIMAP_CONTROL_COLOR};
  }

  html[data-theme="blue-white"] .affine-edgeless-viewport[data-edgeless-preset="system"] .edgeless-background {
    background-color: var(${edgelessCanvasBackgroundVar});
    background-image: ${DARK_CANVAS_SPOTLIGHT_GRADIENT};
  }

  /* E016-S003: Edgeless Blue/White Canvas */
  /* IMPORTANT: Do NOT set --streamlain-v2-layer-background-primary here!
     Databases are children of .edgeless-background and would inherit it.
     Instead, set colors directly; the grid itself is the canvas's own SVG. */
  .affine-edgeless-viewport[data-edgeless-preset="blue-white"] {
    ${edgelessCanvasThemeStyle('blue-white')}
    ${minimapPanelBackgroundVar}: var(${edgelessCanvasBackgroundVar});
    ${minimapControlColorVar}: ${BLUE_WHITE_MINIMAP_CONTROL_COLOR};
  }

  .affine-edgeless-viewport[data-edgeless-preset="blue-white"] .edgeless-background {
    position: relative;
    background-color: var(${edgelessCanvasBackgroundVar});
    background-image: ${DARK_CANVAS_SPOTLIGHT_GRADIENT};
  }

  /* Frame borders in Blue/White - use dark-theme values (white alpha on dark bg) */
  .affine-edgeless-viewport[data-edgeless-preset="blue-white"] {
    --streamlain-v2-edgeless-frame-border-default: #ffffff2b;
  }

  /* The workflow viewer paints the blue-white canvas wherever it mounts. */
  html[data-theme="blue-white"] .workflow-fullscreen-viewport {
    ${edgelessCanvasThemeStyle('blue-white')}
    ${minimapPanelBackgroundVar}: var(${edgelessCanvasBackgroundVar});
    ${minimapControlColorVar}: ${BLUE_WHITE_MINIMAP_CONTROL_COLOR};
  }

`;

let _provided = false;

/**
 * Custom properties the theme editor wrote onto <html>. Only these are
 * removed again: the inline style also carries the boot splash colours and
 * the font size, which a wholesale reset would drop while they are in use.
 */
const themeEditorOverrides = new Set<string>();

function clearThemeEditorOverrides() {
  const style = document.documentElement.style;
  for (const key of themeEditorOverrides) style.removeProperty(key);
  themeEditorOverrides.clear();
}

/** The theme editor stores overrides for the light and dark tables only. */
const hasThemeEditorTable = (name: string): name is 'light' | 'dark' =>
  name === 'light' || name === 'dark';

export const CustomThemeModifier = () => {
  const { themeEditorService, featureFlagService, editorSettingService } =
    useServices({
      ThemeEditorService,
      FeatureFlagService,
      EditorSettingService,
    });
  const enableThemeEditor = useLiveData(
    featureFlagService.flags.enable_theme_editor.$
  );
  const settings = useLiveData(editorSettingService.editorSetting.settings$);
  const { resolvedTheme } = useTheme();

  // Themes without editor tables
  useEffect(() => {
    if (!isThemeName(resolvedTheme) || hasThemeEditorTable(resolvedTheme)) {
      return;
    }

    // Theme colours come from the theme package's [data-theme] scope.
    clearThemeEditorOverrides();
    document.documentElement.style.colorScheme = themeModeOf(resolvedTheme);
  }, [resolvedTheme]);

  // Handle standard themes (light/dark/system) with ThemeEditor support
  useEffect(() => {
    // Skip themes without editor tables
    if (isThemeName(resolvedTheme) && !hasThemeEditorTable(resolvedTheme)) {
      return;
    }

    const mode = resolvedTheme === 'dark' ? 'dark' : 'light';
    clearThemeEditorOverrides();
    document.documentElement.style.colorScheme = mode;

    // Skip if ThemeEditor is disabled
    if (!enableThemeEditor) {
      return;
    }

    if (_provided) return;
    _provided = true;

    const sub = themeEditorService.customTheme$.subscribe(themeObj => {
      if (!themeObj) return;

      const valueMap = themeObj[mode];

      clearThemeEditorOverrides();
      Object.entries(valueMap).forEach(([key, value]) => {
        if (!value) return;
        document.documentElement.style.setProperty(key, value);
        themeEditorOverrides.add(key);
      });
    });

    return () => {
      _provided = false;
      sub.unsubscribe();
    };
  }, [resolvedTheme, enableThemeEditor, themeEditorService]);

  // Apply font size CSS variable when settings change
  useEffect(() => {
    if (settings.fontSize) {
      document.documentElement.style.setProperty(
        '--streamlain-font-base',
        `${settings.fontSize}px`
      );
    }
  }, [settings.fontSize]);

  // E016-S003: Inject global Edgeless Blue/White CSS (always active, regardless of App theme)
  useEffect(() => {
    // Only inject once
    if (document.getElementById(EDGELESS_PRESET_OVERRIDE_ID)) {
      return;
    }

    const styleEl = document.createElement('style');
    styleEl.id = EDGELESS_PRESET_OVERRIDE_ID;
    styleEl.textContent = EDGELESS_BLUE_WHITE_CSS;
    document.head.appendChild(styleEl);

    // No cleanup - this CSS should persist for the entire session
  }, []);

  // Inject the raised chip-label palette once per session so select /
  // multi-select chips + gantt bar fills look saturated instead of
  // washed-out. Paired with the bar renderer dropping its color-mix
  // slate-darken step — the token itself is now strong enough.
  useEffect(() => {
    if (document.getElementById(STRONG_CHIP_LABEL_OVERRIDE_ID)) {
      return;
    }
    const styleEl = document.createElement('style');
    styleEl.id = STRONG_CHIP_LABEL_OVERRIDE_ID;
    styleEl.textContent = STRONG_CHIP_LABEL_CSS;
    document.head.appendChild(styleEl);
  }, []);

  return null;
};
